import { useState, useEffect, useCallback, useRef } from 'react'
import { listConversations, type CommsSource, type ConversationSummary, type InboxCategory } from '@/lib/api'

// Polls a conversation list. Used by:
//  - the inbox (status filter: open / resolved / all; needs-reply is a client filter)
//  - the dashboard badge (default status='open')
//  - the per-customer history screen (customerId + status='all')
//  - the inbox's comms-type filter (source: which system produced the message)
export function useConversations(
  pollMs = 5000,
  opts?: { status?: string; customerId?: string; source?: CommsSource; category?: InboxCategory; needsResponse?: boolean },
) {
  const status = opts?.status ?? 'open'
  const customerId = opts?.customerId
  const source = opts?.source
  const category = opts?.category
  const needsResponse = opts?.needsResponse

  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [needsResponseCount, setNeedsResponseCount] = useState(0)
  const [timezone, setTimezone] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(async () => {
    try {
      const res = await listConversations({ status, customerId, source, category, needsResponse })
      setConversations(res.conversations)
      setNeedsResponseCount(res.needs_response_count)
      setTimezone(res.timezone)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [status, customerId, source, category, needsResponse])

  useEffect(() => {
    setLoading(true)
    refresh()
    timer.current = setInterval(refresh, pollMs)
    return () => { if (timer.current) clearInterval(timer.current) }
  }, [refresh, pollMs])

  return { conversations, needsResponseCount, timezone, loading, error, refresh }
}
